import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useLezioni from '../../hooks/useLezioni';
import CustomCalendar from '../../components/CustomCalendar/CustomCalendar';
import CustomModal from '../../components/CustomModal/CustomModal';
import CardLoadingPage from '../../components/CardLoadingPage/CardLoadingPage';
import ComebackArrow from '../../components/ComebackArrow/ComebackArrow';
import moment from 'moment';
import 'moment/locale/it';
import UtilityService from '../../services/UtillityServices/UtilityService';
import { TIPO_CORSO } from '../../model/tipoCorso';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useSelector } from 'react-redux';
import { ROLES } from '../../model/roles';

export default function AreaPersonaleLezioniPage() {
  const { getLezioniByIdClasse, loadingStateLezioni } = useLezioni();
  const location = useLocation();
  const classe = location.state?.classeData;
  const [lessons, setLessons] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const userLoggedIn = useSelector(state => state?.user?.userData);
  const isSmallScreen = useMediaQuery('(max-width: 1024px)');

  useEffect(() => {
    if (classe?.id_classe) {
      fetchLessons();
    }
  }, [classe]);

  useEffect(() => {
    moment.locale('it');
  }, []);

  const fetchLessons = async () => {
    if (classe?.id_classe) {
      const fetchedLessons = await getLezioniByIdClasse({ id_classe: classe.id_classe });
      if (Array.isArray(fetchedLessons)) {
        const formattedLessons = fetchedLessons.map(lesson => ({
          id_lezione: lesson.id_lezione,
          titolo: lesson.titolo,
          data: lesson.data?.split(' ')[0],
          ora_inizio: lesson.ora_inizio,
          ora_fine: lesson.ora_fine,
          argomenti: lesson.argomenti || [],
          contenuti: lesson.contenuti || [],
          note: lesson.note,
          tipo_lezione: lesson.tipo_lezione
        }));
        setLessons(formattedLessons);
      }
    }
  };

  const handleSelectEvent = (event) => {
    const lesson = lessons.find(l => l.id_lezione === event.id_lezione);
    if (lesson) {
      setSelectedLesson(lesson);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedLesson(null);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const momentDate = moment(date);
    return momentDate.format('dddd D MMMM YYYY').replace(/^\w/, (c) => c.toUpperCase());
  };

  const minDate = classe?.data_inizio ? moment(classe.data_inizio).toDate() : new Date();
  const maxDate = classe?.data_fine ? moment(classe.data_fine).toDate() : new Date();

  if (loadingStateLezioni.getLezioniByIdClasse) {
    return <CardLoadingPage />;
  }

  return (
    <div className={`card custom-card w-100 shadow ${isSmallScreen ? 'py-3 px-0' : 'p-5'}`} style={{ minHeight: "700px" }}>
      <div className="container">
        <ComebackArrow path={userLoggedIn?.role === ROLES.TUTOR ? "/area-personale/classi" : "/area-personale/classe"} state={{classe}}/>
        <div className="d-flex justify-content-between custom-card-header mb-3">
          <div className="d-flex gap-3 custom-card-title my-2">
            <div className={`custom-icon-container`}>
              {classe?.corso?.immagine_corso ? 
                <img src={classe?.corso?.immagine_corso} className='custom-icon' alt='immagine corso'/> : 
                <i className={`bi custom-icon bi-calendar-event fs-2`}></i>}
            </div>
            <div className="align-self-center">
              <span className="fs-4 d-block">
                <i>
                  <b>
                    <h2>{classe?.corso?.denominazione}</h2>
                  </b>
                </i>
              </span>
            </div>
          </div>
        </div>
        <div style={{ height: '100vh' }}>
          <CustomCalendar
            events={lessons.map(lesson => ({
              id_lezione: lesson.id_lezione,
              title: `${lesson.titolo}`,
              start: new Date(`${lesson.data}T${lesson.ora_inizio}`),
              end: new Date(`${lesson.data}T${lesson.ora_fine}`),
              allDay: false
            }))}
            onSelectEvent={handleSelectEvent}
            isLoading={loadingStateLezioni.getLezioniByIdClasse}
            minDate={minDate}
            maxDate={maxDate}
            selectable={false}
            slotCursor="default"
          />
        </div>

        <CustomModal
          show={showModal}
          onHide={handleCloseModal}
          headerText={
            <h5 className="form-control-static">
              {formatDate(selectedLesson?.data)}
            </h5>
          }
          size="lg"
          body={
            selectedLesson && (
              <div className="lesson-details">
                <h3 className="mb-4">{selectedLesson.titolo}</h3>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h5 className="card-title"><i className="bi bi-clock me-2"></i>Orario</h5>
                        <p className="card-text">{selectedLesson.ora_inizio?.split(':').slice(0, 2).join(':')} - {selectedLesson.ora_fine?.split(':').slice(0, 2).join(':')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h5 className="card-title"><i className="bi bi-laptop me-2"></i>Modalità</h5>
                        <p className="card-text">{selectedLesson.tipo_lezione === TIPO_CORSO.TELEMATICA_SINCRONA ? "Telematica sincrona" : "Presenza"}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {selectedLesson.argomenti?.length > 0 && (
                  <div className="mb-4">
                    <h5 className="mb-3"><i className="bi bi-book me-2"></i>Argomenti</h5>
                    <div className="row">
                      {selectedLesson.argomenti?.map(arg => (
                        <div key={typeof arg === 'object' ? arg.id_argomento : arg} className="col-md-4 mb-3">
                          <div className="card h-100 border-primary">
                            <div className="card-body">
                              <p className="card-text">{typeof arg === 'object' ? arg.titolo : `Argomento ID: ${arg}`}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedLesson.contenuti?.length > 0 && ( 
                  <div className="mb-4">
                    <h5 className="mb-3"><i className="bi bi-file-earmark-text me-2"></i>Contenuti</h5>
                    <div className="row">
                      {selectedLesson.contenuti?.map(cont => (
                        <div key={typeof cont === 'object' ? cont.id_contenuto : cont} className="col-md-4 mb-3">
                          <div className="card h-100 border-success">
                            <div className="card-body">
                              <p className="card-text">
                                {typeof cont === 'object' ?          
                                  <>
                                    {UtilityService.generateIconContenuti(cont)}{' '}{cont?.titolo}
                                  </> : `Contenuto ID: ${cont}`}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedLesson.note && (
                  <div className="card bg-light">
                    <div className="card-body">
                      <h5 className="card-title"><i className="bi bi-pencil me-2"></i>Note</h5>
                      <p className="card-text">{selectedLesson.note}</p>
                    </div>
                  </div>
                )}
              </div>
            )
          }
          btnCancelText={'Chiudi'}
          closeButton={true}
          hideButtons={true}
        />
      </div>
    </div>
  );
}
